/**
 * @file `Account` — namespacer for `www.account.*` covering the
 *       AccountMenu + `/authorize` surface on the marketing site.
 * @module @labcraft/www/src/client/account
 *
 * Per the workspace Client-shape rule: `CoreLifecycle` namespacer
 * owned by the parent `Www`. Holds no transport of its own — the
 * parent adopts the shared `HttpTransport` and hands a reference here
 * at construction. Logs and metrics bubble under `www.account.*`.
 *
 * Compared to lab's `Account` namespacer:
 *   - No `updateProfile` / `setChapter` — profile editing and chapter
 *     pointer writes live on lab; the marketing surface is read-only
 *     for identity.
 *   - The `MeRoute` / `SignoutRoute` / `EmailSigninRoute` contracts
 *     come from `@labcraft/account/proxy-routes` (the base proxy
 *     surface every consumer shares); www does not redeclare its own.
 *
 * **Class-name collision** with the loopback `Account` Client from
 * `@labcraft/account` — every callsite that imports both aliases
 * (`import { Account as LoopbackAccount } from '@labcraft/account'`);
 * the www `Account` keeps the bare name per ADR-010's
 * package-name-as-class-name convention.
 */

import { CoreLifecycle } from '@labcraft/core'
import {
  EmailSigninRoute,
  MeRoute,
  SignoutRoute,
  type EmailSigninRequest,
  type MeResponse,
  type SentResponse,
  type SignoutResponse,
} from '@labcraft/account/proxy-routes'
import type { HttpTransport } from './index.ts'

////////////////////////////////////////////////////////////////////////////////
// Types
////////////////////////////////////////////////////////////////////////////////

/**
 * Authenticated-learner view used by www's chrome. Subset of the
 * `MeRoute` learner shape — only the fields the marketing `TopNav`'s
 * `AccountMenu` reads, plus `status` so a future inline CTA can gate on
 * the access state without a separate fetch.
 *
 * @property id - UUID primary key.
 * @property email - Lowercased email address.
 * @property displayName - Free-form display name.
 * @property status - Access state.
 * @property avatarUrl - Provider-hosted profile picture URL, or null
 *   for email-mode learners. The `AccountMenu` falls back to initials.
 */
export type AccountLearner = {
  id: string
  email: string
  displayName: string
  status: 'active' | 'inactive' | 'banned'
  avatarUrl: string | null
}

/**
 * Wire-shape response from `/api/account/me` as the www namespacer
 * surfaces it. Mirrors the proxy's `MeResponse` but narrowed to the
 * `AccountLearner` subset — the chrome doesn't read
 * `currentChapter` / `emailVerifiedAt` / `profileCompletedAt`, so we
 * drop them at this seam to keep the hook layer typed against what's
 * actually consumed.
 */
export type WwwMeResponse = {
  learner: AccountLearner | null
}

////////////////////////////////////////////////////////////////////////////////
// Account namespacer
////////////////////////////////////////////////////////////////////////////////

/**
 * Stateless typed namespacer over the marketing surface's
 * `/api/account/*` HTTP routes. Every method round-trips the daemon;
 * identity caching is the `qkMe` TanStack entry's job (`useMe()`); the
 * 401 → cache flip lives in `main.tsx`.
 */
export class Account extends CoreLifecycle<{ id?: string }> {
  private readonly http: HttpTransport

  constructor(http: HttpTransport) {
    super({ id: 'account' })
    this.http = http
  }

  /**
   * `GET /api/account/me` — returns the authenticated learner, or
   * `{ learner: null }` for an anonymous request. The narrow
   * {@link WwwMeResponse} shape is what `useMe()` exposes to chrome
   * components; we don't surface the lab-only fields the proxy
   * passthrough may carry.
   */
  async me(): Promise<WwwMeResponse> {
    const res: MeResponse = await this.http.request(MeRoute)
    if (!res.learner) return { learner: null }
    return {
      learner: {
        id: res.learner.id,
        email: res.learner.email,
        displayName: res.learner.displayName,
        status: res.learner.status,
        avatarUrl: res.learner.avatarUrl,
      },
    }
  }

  /**
   * `POST /api/account/email/authorize` — mail a magic-link auth token
   * to `args.email`. Sign-up and sign-in collapse to the same call —
   * account-service creates a fresh `learners` row on first sighting
   * and logs in an existing learner on subsequent calls.
   *
   * Always resolves with `{ status: 'sent' }` regardless of whether the
   * address exists — the silent-`sent` shape blocks enumeration leaks.
   *
   * The magic-link verify URL embedded in the email lands on lab; the
   * SSO cookie minted at verify time travels back to www on the next
   * page load.
   *
   * @param args - Email + optional `next` redirect target. Server
   *   sanitizes `next`.
   */
  async signinWithEmail(args: EmailSigninRequest): Promise<SentResponse> {
    return this.http.request(EmailSigninRoute, { body: args })
  }

  /**
   * `POST /api/account/signout` — clears the cookie + Redis session.
   * The caller's `useSignoutMutation.onSuccess` flips `qkMe` to
   * `{ learner: null }` synchronously to avoid a chrome-bounce.
   */
  async signout(): Promise<SignoutResponse> {
    return this.http.request(SignoutRoute)
  }
}
